"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";
import { AgenticMinimalChat, ChatUiEventTypes, type ChatUiEvent } from "@trimble-agentic-external-npm-local/agentic-chat-react";
import { assistAgentId, assistEnvironment } from "@/lib/config/environments";
import { OPEN_ASSIST_FLYOUT_EVENT } from "@/lib/assist/assistFlyoutEvents";
import { toAssistLocale } from "@/lib/assist/assistLocale";
import { useAssistToken } from "@/lib/assist/useAssistToken";
import { usePromotionBannerOpen } from "@/lib/promotionBannerOpenStore";
interface FrontdoorAgentFlyoutProps {
  lang?: string;
  /**
   * Set to false when another surface already offers a way in -- the
   * trimble-developer build opens the flyout from TDHeader's assistant icon
   * (via OPEN_ASSIST_FLYOUT_EVENT), so the floating button would be a second
   * control for the same panel.
   */
  showLauncher?: boolean;
}
export default function FrontdoorAgentFlyout(t0) {
  const $ = _c(20);
  const {
    lang,
    showLauncher: t1
  } = t0;
  const showLauncher = t1 === undefined ? true : t1;
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const provideToken = useAssistToken();
  const isPromotionBannerOpen = usePromotionBannerOpen();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setHasOpened(true);
      setIsOpen(true);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const open = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsOpen(false);
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const close = t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event => {
      if (event.type === ChatUiEventTypes.OnClose) {
        close();
      }
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const handleEvent = t4;
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      window.addEventListener(OPEN_ASSIST_FLYOUT_EVENT, open);
      return () => window.removeEventListener(OPEN_ASSIST_FLYOUT_EVENT, open);
    };
    t6 = [open];
    $[3] = t5;
    $[4] = t6;
  } else {
    t5 = $[3];
    t6 = $[4];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[5] !== isOpen) {
    t7 = () => {
      if (!isOpen) {
        return;
      }
      const onKeyDown = event_0 => {
        if (event_0.key === "Escape") {
          close();
        }
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    };
    t8 = [isOpen, close];
    $[5] = isOpen;
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  useEffect(t7, t8);
  if (!assistAgentId) {
    return null;
  }
  let t9;
  if ($[8] !== isOpen || $[9] !== isPromotionBannerOpen || $[10] !== showLauncher) {
    t9 = showLauncher && <button type="button" onClick={open} aria-label="Open Trimble Assist" data-promotion-banner-open={isPromotionBannerOpen} className={`mxp-assist-fab fixed right-6 z-20 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full border-0 bg-blue-trmb text-white shadow-lg transition-[opacity,bottom] duration-300 ease-in-out hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-trmb focus-visible:ring-offset-2 ${isOpen ? "pointer-events-none opacity-0" : "opacity-100"} ${isPromotionBannerOpen ? "bottom-[calc(var(--promotion-banner-height,0px)+1.5rem)]" : "bottom-6"}`}><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /><path d="M12 7v6" /><path d="M9 10h6" /></svg></button>;
    $[8] = isOpen;
    $[9] = isPromotionBannerOpen;
    $[10] = showLauncher;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== hasOpened || $[13] !== isOpen || $[14] !== lang || $[15] !== provideToken) {
    t10 = hasOpened && <aside aria-label="Trimble Assist" aria-hidden={!isOpen} inert={!isOpen} className={`fixed inset-y-0 right-0 z-50 flex h-dvh w-full max-w-[350px] flex-col bg-white shadow-[-5px_0_25px_rgba(0,0,0,0.15)] transition-transform duration-300 ease-out ${isOpen ? "pointer-events-auto" : "pointer-events-none"}`} style={{
      transform: isOpen ? "translateX(0)" : "translateX(100%)"
    }}><button type="button" onClick={close} aria-label="Close Trimble Assist" className="mxp-flyout-close absolute top-4 right-4 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 bg-black/5 text-gray-800 transition-colors duration-200 hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-trmb"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg></button><AgenticMinimalChat provideToken={provideToken} onEvent={handleEvent} environment={assistEnvironment} theme="light" agentId={assistAgentId} locale={toAssistLocale(lang)} chatInputConfiguration={{
        buttons: [],
        hideModelSelection: true
      }} className="h-full w-full border-0" /></aside>;
    $[12] = hasOpened;
    $[13] = isOpen;
    $[14] = lang;
    $[15] = provideToken;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
}